import type { Database } from '@kit/supabase/database';

/**
 * Canonical gender registry — the SINGLE source of truth for the `gender_type`
 * enum's values, display labels, and selectable option sets (Critical Rule #8:
 * one edit surface, no parallel gender maps).
 *
 * Before this existed, gender was hand-maintained in four places that had
 * already drifted — `nonBinary` was labeled `'Non-Binary'` (artists),
 * `'Diverse'` (auditions booklet/XLSX export) and `'Non-binary'` (booklet
 * field). Every consumer (profile constants, the competition apply structure,
 * the booklet field map, the offering-data label formatter) now imports from
 * here.
 */
export type GenderValue = Database['public']['Enums']['gender_type'];

export interface GenderOption {
  value: GenderValue;
  label: string;
}

/** All `gender_type` enum values, in display order. */
export const GENDER_VALUES: GenderValue[] = ['male', 'female', 'nonBinary'];

/** Canonical English display label per gender value. */
export const GENDER_LABELS: Record<GenderValue, string> = {
  male: 'Male',
  female: 'Female',
  nonBinary: 'Non-binary',
};

/** Full option list (value + label) for display / select surfaces. */
export const GENDER_OPTIONS: GenderOption[] = GENDER_VALUES.map((value) => ({
  value,
  label: GENDER_LABELS[value],
}));

/**
 * Subset offered when ENTERING a competition: gendered competition categories
 * are binary, so only male/female are selectable. Existing `nonBinary` data is
 * still labeled (via {@link GENDER_LABELS}) in displays, exports and booklets.
 */
export const COMPETITION_GENDER_VALUES: GenderValue[] = ['male', 'female'];

export const COMPETITION_GENDER_OPTIONS: GenderOption[] =
  COMPETITION_GENDER_VALUES.map((value) => ({
    value,
    label: GENDER_LABELS[value],
  }));

/**
 * Legacy stored variants that predate this registry. The audition
 * requirements form wrote `'non-binary'` (kebab) into `requirement_genders`
 * before it derived its options from here (fixed 2026-07-03); rows written
 * in that window still need a label.
 */
const LEGACY_GENDER_ALIASES: Record<string, string> = {
  'non-binary': GENDER_LABELS.nonBinary,
};

/**
 * Maps a stored gender value to its display label. Unknown values pass through
 * unchanged; null/undefined/empty yields ''. The single label formatter for
 * offering-data readers (XLSX export, PDF booklet) and any other display.
 */
export function formatGenderLabel(gender: string | null | undefined): string {
  if (!gender) return '';
  return (
    (GENDER_LABELS as Record<string, string>)[gender] ??
    LEGACY_GENDER_ALIASES[gender] ??
    gender
  );
}
